/**
 * Componente Modal reutilizable
 * Fecha: 16 Oct 2025
 */

import React, { Fragment } from 'react';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';

interface ModalProps {
  /** Si el modal está abierto */
  isOpen: boolean;
  /** Función para cerrar el modal */
  onClose: () => void;
  /** Título del modal */
  title?: string;
  /** Descripción del modal */
  description?: string;
  /** Contenido del modal */
  children: React.ReactNode;
  /** Tamaño del modal */
  size?: ModalSize;
  /** Footer del modal (acciones) */
  footer?: React.ReactNode;
  /** Si false, no muestra el botón X de cerrar */
  showCloseButton?: boolean;
  /**Para mostrar close button custom */
  customCloseButton?: React.ReactNode;
  /** Si false, no permite cerrar al hacer clic fuera */
  closeOnOutsideClick?: boolean;
  /** Clases adicionales para el contenido */
  className?: string;
  /** Propiedad para esconder border bottom del titulo */
  showBorderTitle?: boolean;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-full mx-4',
};

/**
 * Modal component usando Headless UI
 * 
 * @example
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirmar acción"
 *   footer={<ModalActions />}
 * >
 *   <p>¿Estás seguro de que deseas continuar?</p>
 * </Modal>
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  footer,
  showCloseButton = true,
  closeOnOutsideClick = true,
  className = '',
  customCloseButton,
  showBorderTitle = true
}) => {
  const handleClose = () => {
    if (closeOnOutsideClick) {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`
                  w-full ${sizeClasses[size]}
                  transform overflow-hidden rounded-lg
                  bg-white dark:bg-gray-800
                  text-left align-middle shadow-xl
                  transition-all
                `}
              >
                {/* Header */}
                {(title || showCloseButton) && (
                  <div className={`flex items-start bg-[#ffffff] justify-between ${showBorderTitle ? 'border-b border-gray-200 dark:border-gray-700' : ''} px-6 py-4`}>
                    <div className="flex-1">
                      {title && (
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-semibold leading-6 text-gray-900 dark:text-white"
                        >
                          {title}
                        </Dialog.Title>
                      )}
                      {description && (
                        <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {description}
                        </Dialog.Description>
                      )}
                    </div>
                    {showCloseButton && (
                      <button
                        type="button"
                        onClick={onClose}
                        className="ml-4 bg-[#D9D9D9]/50 rounded-full p-1 text-[#BDBDBD] hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2"
                      >
                        <span className="sr-only">Cerrar</span>
                        <X className="h-5 w-5" />
                      </button>
                    )}
                    {customCloseButton}
                  </div>
                )}

                {/* Content */}
                <div className={`px-6 py-4 bg-[#ffffff] ${className}`}>{children}</div>

                {/* Footer */}
                {footer && (
                  <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

/**
 * Modal de confirmación pre-configurado
 */
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'info',
  isLoading = false,
}) => {
  const variantes = {
    danger: 'peligro',
    warning: 'primario',
    info: 'primario',
  } as const;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className={estiloDeBoton('secundario')}
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={estiloDeBoton(variantes[variant])}
          >
            {isLoading ? 'Procesando...' : confirmText}
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">{message}</p>
    </Modal>
  );
};
